import { Link } from 'react-router-dom'

import { PageState } from '../app/PublicLayout.jsx'
import { useCollection } from '../app/useSiteData.js'
import { useI18n } from '../lib/i18n.jsx'
import { Container, EmptyState, Grid, PageHead, Section, SectionHead } from '../ui/index.js'
import { DemoCard } from './DemoList.jsx'
import { ResearchCard } from './ResearchList.jsx'

/**
 * Research: the overview page.
 *
 * Two bands — the projects the group leads, and the demos its work produces —
 * each showing at most two rows with a link to the full list. A visitor should
 * be able to see both categories exist without scrolling past thirty cards of
 * the first one, which is what a single long list does.
 *
 * The two-row limit is enforced in CSS rather than by a fixed item count,
 * because how many cards make a row depends on the viewport. The slice here is
 * only a cap on how much is rendered at all — the grid can never need more than
 * eight to fill two rows at the widest breakpoint.
 *
 * 研究方向总览页。
 *
 * 两条带子——课题组主持的项目，以及这些工作做出来的演示样例——各自最多两行，
 * 并带一个通往完整列表的链接。访客应当能同时看见这两个类别的存在，
 * 而不是先滚过第一类的三十张卡片才发现还有第二类；后者正是单一长列表的结果。
 *
 * 两行的限制由 CSS 实施，而不是靠写死条数，因为「一行能放几张」取决于视口宽度。
 * 这里的截断只是限制「总共渲染多少」——最宽的断点下，填满两行也用不到八张以上。
 *
 * @returns {JSX.Element} Page / 页面。
 */

/** Most cards any breakpoint needs to fill two rows. 任何断点填满两行所需的卡片上限。 */
const TWO_ROWS_MAX = 8

export default function ResearchOverview() {
  const { t } = useI18n()
  const projects = useCollection('research')
  const demos = useCollection('demos')

  const projectItems = (projects.data || []).slice(0, TWO_ROWS_MAX)
  const demoItems = (demos.data || []).slice(0, TWO_ROWS_MAX)

  const hasMoreProjects = (projects.data || []).length > projectItems.length
  const hasMoreDemos = (demos.data || []).length > demoItems.length

  return (
    <Section className="research-page">
      <Container>
        <PageHead title={t('research.overviewTitle')} lede={t('research.overviewLede')} />

        {/* -- Projects / 研究项目 -- */}
        <section className="research-band">
          <SectionHead
            title={t('research.title')}
            aside={
              <Link className="research-band__more" to="/research/projects">
                {t('common.viewAll')}
              </Link>
            }
          />
          <PageState
            loading={projects.loading}
            error={projects.error}
            onRetry={projects.reload}
            isEmpty={!projectItems.length}
            empty={<EmptyState title={t('research.empty')} body={t('research.emptyBody')} />}
          >
            {/* `--two-rows` marks a grid the theme should clamp to two rows at
                every breakpoint. The page says "two rows"; the theme knows how
                many cards that is.
                --two-rows 标记「主题应在每个断点把它限制为两行」的栅格。
                页面只说「两行」，一行几张由主题决定。 */}
            <Grid className="research-grid research-grid--two-rows">
              {projectItems.map((record) => (
                <ResearchCard key={record.id} record={record} />
              ))}
            </Grid>
            {hasMoreProjects && (
              <p className="research-band__foot">
                <Link className="link-button link-button--ghost" to="/research/projects">
                  {t('research.viewAllProjects')}
                </Link>
              </p>
            )}
          </PageState>
        </section>

        {/* -- Demos / 演示样例 -- */}
        <section className="research-band">
          <SectionHead
            title={t('demos.title')}
            aside={
              <Link className="research-band__more" to="/research/demos">
                {t('common.viewAll')}
              </Link>
            }
          />
          <PageState
            loading={demos.loading}
            error={demos.error}
            onRetry={demos.reload}
            isEmpty={!demoItems.length}
            empty={<EmptyState title={t('demos.empty')} body={t('demos.emptyBody')} />}
          >
            <Grid className="demo-grid demo-grid--two-rows">
              {demoItems.map((record) => (
                <DemoCard key={record.id} record={record} />
              ))}
            </Grid>
            {hasMoreDemos && (
              <p className="research-band__foot">
                <Link className="link-button link-button--ghost" to="/research/demos">
                  {t('demos.viewAll')}
                </Link>
              </p>
            )}
          </PageState>
        </section>
      </Container>
    </Section>
  )
}
